import { ColumnDef } from "@commonlibs";
import type { OrganizationPointDto } from "common/models/api/models/OrganizationPointDto";
import useIsMobile from "ui/common/hooks/useIsMobile";
import useTranslate from "ui/locale/hooks/useTranslate";
import { POINT_COLUMNS } from "./usePointColumns.types";
import PointsBalance from "ui/wallet/containers/PointsBalance/PointsBalance";
import PointsBalanceText from "ui/wallet/containers/PointsBalance/PointsBalanceText";
import { spacing } from "ui/config/theme/spacing/spacing";
import ViewAndUpdateTyCIcons from "ui/terms/containers/UpdateTermsAndPrivacyModal/ViewAndUpdateTyCIcons";
import {
    UpdateTermsAndPrivacyName,
    ViewTermsAndPrivacyType,
} from "ui/terms/containers/UpdateTermsAndPrivacyModal/UpdateTermsAndPrivacyModal.types";
import AddPointsModal from "ui/point/containers/AddPointsModal/AddPointsModal";
import PointsBalanceActions from "ui/wallet/containers/PointsBalance/PointsBalanceActions";
import UpdateConversionRateModal from "ui/point/containers/UpdateConversionRateModal/UpdateConversionRateModal";
import UpdateCaducityModal from "ui/point/containers/UpdateCaducityModal/UpdateCaducityModal";

export function usePointColumns(): ColumnDef<OrganizationPointDto>[] {
    const translate = useTranslate();
    const isMobile = useIsMobile();

    return [
        {
            accessorKey: POINT_COLUMNS.MINTED,

            header: translate("totalMinted"),
            alignHeader: "left",
            align: "left",
            size: isMobile ? 80 : 90,
            cell: (info) => info.getValue(),
            accessorFn: (row) => <PointsBalanceActions balance={row.minted} modal={AddPointsModal} variant="body2Regular" />,
        },
        {
            accessorKey: POINT_COLUMNS.AVAILABLE,
            size: isMobile ? 50 : 60,
            header: translate("availabled"),
            alignHeader: "left",
            align: "left",
            cell: (info) => info.getValue(),
            accessorFn: (row) => <PointsBalance balance={row.available} variant="body2Regular" />,
        },
        {
            accessorKey: POINT_COLUMNS.CIRCULATING,
            size: isMobile ? 50 : 60,
            header: translate("circulating"),
            alignHeader: "left",
            align: "left",
            cell: (info) => info.getValue(),
            accessorFn: (row) => <PointsBalance balance={row.circulating} variant="body2Regular" />,
        },
        {
            accessorKey: POINT_COLUMNS.RATE,
            size: isMobile ? 50 : 60,
            header: translate("rate"),
            alignHeader: "left",
            align: "left",
            cell: (info) => info.getValue(),
            accessorFn: (row) => (
                <PointsBalanceText
                    balance={String(row.conversionRate)}
                    text={"a 1"}
                    modal={UpdateConversionRateModal}
                    variant="body2Regular"
                    gab={spacing["1.5"]}
                />
            ),
        },
        {
            accessorKey: POINT_COLUMNS.EXPIRATION,
            size: isMobile ? 50 : 60,
            header: translate("expiration"),
            alignHeader: "left",
            align: "left",
            cell: (info) => info.getValue(),
            accessorFn: (row) => (
                <PointsBalanceText
                    balance={String(row.caducityDays)}
                    text={translate("days")}
                    modal={UpdateCaducityModal}
                    variant="body2Regular"
                    formatter="days"
                />
            ),
        },
        {
            accessorKey: POINT_COLUMNS.TERMS_AND_CONDITIONS,
            size: isMobile ? 60 : 70,
            header: () => (
                <>
                    Términos
                    <br />y condiciones
                </>
            ),
            alignHeader: "left",
            align: "left",
            cell: (info) => info.getValue(),
            accessorFn: () => (
                <ViewAndUpdateTyCIcons
                    title={translate("termsAndConditions")}
                    fileName={UpdateTermsAndPrivacyName.TERMS}
                    fileType={ViewTermsAndPrivacyType.TERMS}
                />
            ),
        },
        {
            accessorKey: POINT_COLUMNS.PRIVACY_POLICY,
            size: isMobile ? 60 : 70,
            header: () => (
                <>
                    Política
                    <br />
                    de privacidad
                </>
            ),
            alignHeader: "left",
            align: "left",
            cell: (info) => info.getValue(),
            accessorFn: () => (
                <ViewAndUpdateTyCIcons
                    title={translate("privacyPolicy")}
                    fileName={UpdateTermsAndPrivacyName.PRIVACY}
                    fileType={ViewTermsAndPrivacyType.PRIVACY}
                />
            ),
        },
        {
            accessorKey: POINT_COLUMNS.PERSONAL_DATA_PROCESSING,
            size: isMobile ? 70 : 80,
            header: () => (
                <>
                    Tratamiento de
                    <br />
                    datos personales
                </>
            ),
            alignHeader: "left",
            align: "left",
            cell: (info) => info.getValue(),
            accessorFn: () => (
                <ViewAndUpdateTyCIcons
                    title={translate("personalDataProcessing")}
                    fileName={UpdateTermsAndPrivacyName.PERSONAL_DATA_TREATMENT}
                    fileType={ViewTermsAndPrivacyType.PERSONAL_DATA_TREATMENT}
                />
            ),
        },
    ];
}
